import { Formik, Form } from "formik";
import React, { useEffect, useState } from "react";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import {
  ICFormikDropDown,
  ICFormikTextInput,
} from "../../core-component/formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  handleError,
  hasAnyModification,
  sweetAlertError,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";
import { IMeasurementField } from "./MeasurementFieldListController";
import {
  fetchMeasurementFieldById,
  getMeasurementTypeUnitDdl,
  handleSubmitMeasurementField,
  IMeasurementFieldForm,
  MEASUREMENT_FIELD_INITIAL_VALUES,
  measurementFieldValidationSchema,
} from "./MeasurementFieldFormController";
import { IOption } from "../../data/AppInterface";
import { ICFileUpload } from "../../core-component/form";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
import { fetchUploadImageService } from "../global/image-service/ImageServices";
import { IMAGE_TYPE } from "../../utils/AppEnumerations";

interface IMeasurementFieldFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  measurementFieldToEdit?: IMeasurementField;
}

const MeasurementFieldForm: React.FC<IMeasurementFieldFormProps> = ({
  handleClose,
  measurementFieldToEdit,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [measurementTypeUnit, setMeasurementTypeUnit] = useState<
    IOption<string>[]
  >([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [typeImage, setTypeImage] = useState<string[]>([]);
  const [
    measurementFieldFormInitialValues,
    setMeasurementFieldFormInitialValues,
  ] = useState<IMeasurementFieldForm>();

  useEffect(() => {
    if (measurementFieldToEdit) {
      getMeasurementFieldById(measurementFieldToEdit.id);
    } else {
      setMeasurementFieldFormInitialValues(MEASUREMENT_FIELD_INITIAL_VALUES);
      fetchMeasurementTypeUnit();
    }
  }, []);

  const getMeasurementFieldById = async (id: string) => {
    setLoading(true);
    const measurementUnit = await fetchMeasurementFieldById(id, handleClose);
    setMeasurementFieldFormInitialValues(measurementUnit);
    fetchMeasurementTypeUnit(measurementUnit.measurement_unit);
    setLoading(false);
  };

  const fetchMeasurementTypeUnit = async (MeasurementUnitValue?: string) => {
    setLoading(true);
    await getMeasurementTypeUnitDdl(
      setMeasurementTypeUnit,
      MeasurementUnitValue
    );
    setLoading(false);
  };

  const checkFormModification = (values: IMeasurementFieldForm) => {
    if (
      measurementFieldFormInitialValues &&
      hasAnyModification(values, measurementFieldFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };
  useEffect(() => {
    setTypeImage(
      measurementFieldFormInitialValues?.measurement_field_image_url
        ? [measurementFieldFormInitialValues.measurement_field_image_url]
        : []
    );
  }, [measurementFieldFormInitialValues]);

  const handleSubmit = async (values: IMeasurementFieldForm) => {
    try {
      setSubmitLoading(true);
      const updatedTypeMedia = typeImage.map((url) => ({
        media_url: url,
      }));

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("ImageFile", selectedFiles[0]);
        formData.append("type", IMAGE_TYPE.measurementField);
        if (values.id) {
          formData.append("typeId", values.id.toString());
        }

        const mediaUrl = await fetchUploadImageService(formData);
        if (!mediaUrl) {
          return null;
        }

        updatedTypeMedia.push({ media_url: mediaUrl });
      }

      await handleSubmitMeasurementField(
        {
          ...values,
          measurement_field_image_url: updatedTypeMedia[0]?.media_url || "",
        },
        setSubmitLoading,
        measurementFieldToEdit?.id,
        handleClose
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "measurementFieldSubmit");
    } finally {
      setSubmitLoading(false);
    }
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {measurementFieldFormInitialValues && (
            <Formik
              initialValues={measurementFieldFormInitialValues}
              validationSchema={measurementFieldValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("measurement_field")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"measurement_field_name"}
                          placeholder={t("measurement_field_name")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikDropDown
                          {...formikPorps}
                          name={"measurement_unit"}
                          placeholder={t("measurement_unit")}
                          options={measurementTypeUnit}
                          required
                          disabled={readOnly}
                        />
                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_image")}
                            maxFiles={1}
                            maxFileSizeMB={10}
                            acceptedFileTypes={["jpg", "png", "jpeg"]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={typeImage}
                            setData={setTypeImage}
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default MeasurementFieldForm;
